import {isBefore, parseISO} from 'date-fns'
import {arrayEquals, UTCToDate} from '@/utils/helpers'
import {persistentStore, runtimeStore} from '@/composables/store-accessor'
import {
    filters as blankFilters,
    groupOffersBy as blankGroupOffersBy,
    searchRequest as blankSearchRequest,
    searchResponse as blankSearchResponse,
} from '~src/utils/hotels/hotels-blank-states.src'
import {salesTermsRsTransformer} from '@/utils/api-helpers'
import {convertPrice} from '@/utils/filters'
import {appInstance} from '@/utils/app-accessor'
import {calcTotalPrice} from '@/utils/price-utils'
import {prepareBookResult} from '@/utils/PrepareBookAdapter'
import {useProductStore} from '~/composables/product-store'
import type {Ref} from 'vue'
import {EventBus} from '~/utils/event-bus'

const blankSingleHotelSearchResponse = () => ({
    offers: [],
})

export const useHotelsStore = defineStore('hotels', () => {
    const searchRequest: Ref = ref({})
    const searchResponse = ref(blankSearchResponse())
    const searchExpirationTime: Ref = ref(null)
    const filters: Ref = ref(blankFilters())
    const sortFnName = ref('recommended')
    const groupOffersBy = ref(blankGroupOffersBy)
    const singleHotelSearchRequest = ref(null)
    const singleHotelSearchExpirationTime: Ref = ref(null)
    const singleHotelSearchResponse = ref(blankSingleHotelSearchResponse())
    const conditions: Ref = ref({})
    const tariffDetails: Ref = ref({})
    const tourists: Ref<any[]> = ref([])
    const prepareBookRequest: Ref<any[]> = ref([])
    const prepareBookResponse = ref({})
    const offersToCompare: Ref<any[]> = ref([])
    const bookingAdditionalOptions: Ref<any[]> = ref([])
    const basket: Ref<any[]> = ref([])
    const checkInOutExtraService: Ref<any[]> = ref([])
    const mapPosition = ref({})
    const recentHotels: Ref<any[]> = ref([])

    function SET_SEARCH_REQUEST(rq: any) {
        searchRequest.value = rq
    }

    function RESET_FILTERS() {
        filters.value = blankFilters()
    }

    function SET_SORT_FN_NAME(sort: any) {
        sortFnName.value = sort
    }

    function SET_GROUP_OFFERS_BY(val: any) {
        groupOffersBy.value = val
    }

    function SET_FILTER({key, value}: any) {
        filters.value[key] = value
    }

    function SET_SINGLE_HOTEL_SEARCH_REQUEST(rq: any) {
        singleHotelSearchRequest.value = rq
    }

    function SET_SINGLE_HOTEL_SEARCH_RESPONSE(rs: any) {
        singleHotelSearchResponse.value = rs
    }

    function SET_CONDITIONS(_conditions: any) {
        conditions.value[_conditions.offerKey] = _conditions
    }

    function SET_TARIFF_DETAILS(_tariffDetails: any) {
        tariffDetails.value[_tariffDetails.offerKey] = _tariffDetails
    }

    function SET_TOURIST_PROP({roomIndex, index, prop, val}: any) {
        tourists.value[roomIndex][index][prop] = val
    }

    function SET_TOURIST_PASSPORT_PROP({roomIndex, index, prop, val}: any) {
        tourists.value[roomIndex][index].passport[prop] = val
    }

    function SET_TOURIST({roomIndex, index, val}: any) {
        if (!tourists.value[roomIndex]) {
            tourists.value[roomIndex] = []
        }
        tourists.value[roomIndex][index] = val
    }

    function SET_TOURISTS(_tourists: any) {
        tourists.value = _tourists
    }

    function SET_PREPARE_BOOK_REQUEST(_prepareBookRequest: any) {
        prepareBookRequest.value = _prepareBookRequest
    }

    function SET_PREPARE_BOOK_REQUEST_PROP({index, prop, val}: any) {
        if (!prepareBookRequest.value[index]) {
            prepareBookRequest.value[index] = {}
        }
        prepareBookRequest.value[index][prop] = val
    }

    function SET_PREPARE_BOOK_RESPONSE(_prepareBookResponse: any) {
        prepareBookResponse.value = _prepareBookResponse
    }

    function SET_SEARCH_EXPIRATION_TIME(date: any) {
        searchExpirationTime.value = date
    }

    function SET_SINGLE_HOTEL_SEARCH_EXPIRATION_TIME(date: any) {
        singleHotelSearchExpirationTime.value = date
    }

    function SELECT_TO_COMPARE(hotelCode: any) {
        const index = offersToCompare.value.findIndex((hotel: any) => hotel.hotelCode === hotelCode)
        if (index !== -1) {
            offersToCompare.value.splice(index, 1)
        } else {
            offersToCompare.value.push({hotelCode, rooms: []})
        }
    }

    function ADD_ROOM_TO_COMPARE({hotelCode, rooms}: any) {
        const index = offersToCompare.value.findIndex((hotel: any) => hotel.hotelCode === hotelCode)
        if (!rooms?.length) {
            if (index !== -1) {
                offersToCompare.value.splice(index, 1)
            }
        } else {
            if (index !== -1) {
                offersToCompare.value[index].rooms = rooms
            } else {
                offersToCompare.value.push({hotelCode, rooms})
            }
        }
    }

    function CLEAR_OFFERS_TO_COMPARE() {
        offersToCompare.value = []
    }

    function SET_BOOKING_ADDITIONAL_OPTIONS(options: any) {
        bookingAdditionalOptions.value = options
    }

    function SET_CHECK_IN_OUT_EXTRA_SERVICE({roomIndex, type, time, serviceRPH, price, convertedPrice}: any) {
        const alreadyGot = checkInOutExtraService.value[roomIndex] || {}
        const extraService = {
            [type]: {
                serviceRPH,
                time,
                price,
                convertedPrice,
                roomIndex,
                text: type,
            },
        }
        checkInOutExtraService.value[roomIndex] = {...alreadyGot, ...extraService}
    }

    function DELETE_CHECK_IN_OUT_EXTRA_SERVICE({roomIndex, type}: any) {
        // TODO [P1] Need check
        delete checkInOutExtraService.value[roomIndex][type]
    }

    function ADD_TO_BASKET(offerKeys: any) {
        basket.value = basket.value.filter((item: any) =>
            isBefore(new Date(), UTCToDate(parseISO(item.offer.expirationTime)))
        )
        if (basket.value.findIndex(item => arrayEquals(item.offerKeys, offerKeys)) !== -1) return
        const roomOffers: any[] = [],
            agreedOfferKeys: any[] = []
        const findHotelAndRoomOffers = (offers: any[]) => {
            return offers.find(offer => {
                offer.rooms.forEach((room: {groupedOffers: any[]}) => {
                    offerKeys.forEach((offerKey: any) => {
                        const selectedRoomOffer = room.groupedOffers.find(
                            groupedOffer => groupedOffer.offerKey === offerKey
                        )
                        if (selectedRoomOffer) {
                            const index = selectedRoomOffer.rph - 1
                            roomOffers[index] = room
                            agreedOfferKeys[index] = offerKey
                        }
                    })
                })
                return !!roomOffers.length
            })
        }
        let offer, _searchRequest
        if (singleHotelSearchResponse.value) {
            offer = findHotelAndRoomOffers(singleHotelSearchResponse.value.offers)
        }
        if (offer) {
            _searchRequest = Object.assign({}, singleHotelSearchRequest.value)
        } else {
            //TODO For HDI need load offers from cache
            offer = findHotelAndRoomOffers(hotelsRuntimeStore.filteredOffers)
            _searchRequest = Object.assign({}, searchRequest.value)
        }
        basket.value.push({offerKeys: agreedOfferKeys, offer, roomOffers, searchRequest: _searchRequest})
    }

    //TODO Need refactoring with other products
    function REFRESH_BASKET_PRICE({offerKey, prepareBookResponse}: any) {
        const basketItem = basket.value.find(item => item.offerKeys.includes(offerKey))
        if (!basketItem) return
        const roomOffer = basketItem.roomOffers.find(
            (room: {groupedOffers: any[]}) =>
                room.groupedOffers.findIndex(groupedOffer => groupedOffer.offerKey === offerKey) !== -1
        )
        const {price} = salesTermsRsTransformer(prepareBookResponse.currentSalesTerms)
        if (prepareBookResponse?.paymentByLoyaltyPoint?.paidByLoyalty) {
            // @ts-ignore
            price.loyaltyAmount = prepareBookResponse.paymentByLoyaltyPoint.paidByLoyalty
        }
        roomOffer.currentPrice = price
    }

    function REFRESH_BASKET_ROOM_OFFERS({index, roomOffers, offerKeys, offer}: any) {
        const {searchRequest} = basket.value[index]
        basket.value.push({
            offer,
            searchRequest,
            roomOffers,
            offerKeys,
        })
    }

    function NEW_SEARCH(_searchRequest: any) {
        searchRequest.value = _searchRequest
        offersToCompare.value = []
        filters.value = blankFilters()
        searchExpirationTime.value = null
        mapPosition.value = {}
    }

    function RESET() {
        RESET_SEARCH()
        tourists.value = []
        prepareBookRequest.value = []
    }

    function RESET_SEARCH() {
        searchRequest.value = blankSearchRequest()
        singleHotelSearchRequest.value = null
        mapPosition.value = {}
    }

    function SET_MAP_POSITION(data: any) {
        mapPosition.value = data
    }

    function NEW_SINGLE_HOTEL_SEARCH(searchRequest: any) {
        singleHotelSearchRequest.value = searchRequest
        singleHotelSearchResponse.value = blankSingleHotelSearchResponse()
        singleHotelSearchExpirationTime.value = null
    }

    function SET_RECENT_HOTELS(val: any) {
        const _recentHotels = recentHotels.value.reverse()
        const hotelIndex = _recentHotels.findIndex(hotel => hotel.hotelCode === val.hotelCode)

        if (hotelIndex === -1) _recentHotels.push(val)
        else _recentHotels[hotelIndex] = val

        recentHotels.value = _recentHotels.reverse().slice(0, 5)
    }

    function clearBookingAdditionalOptions() {
        bookingAdditionalOptions.value = []
        checkInOutExtraService.value = []
    }

    async function getTariffDetails(rq: any) {
        let data = {
            offerKey: rq.offerKey,
            loading: true,
        }
        try {
            SET_TARIFF_DETAILS(data)
            const rs = await appInstance.$api.tariffDetails.get(rq)
            Object.assign(data, rs, {loading: false})
            SET_TARIFF_DETAILS(data)
        } catch (e: any) {
            if (e.status === 404) EventBus.$emit('OFFER_EXPIRED_EVENT')
            Object.assign(data, {
                loading: false,
                errors: ['Offer was expired'],
            })
            SET_TARIFF_DETAILS(data)
        }
    }

    async function prepareBook(rq: any) {
        runtimeStore.SET_BOOKING_ACTIVE(true)
        try {
            SET_PREPARE_BOOK_REQUEST(rq)
            const rs = await appInstance.$api.prepareAccommodationBook.post(rq)
            SET_PREPARE_BOOK_RESPONSE(rs)
            return rs
        } catch (e: any) {
            if (e.status === 404) EventBus.$emit('OFFER_EXPIRED_EVENT')
        } finally {
            runtimeStore.SET_BOOKING_ACTIVE(false)
        }
    }

    //TODO Move to runtime
    async function loadBookingAdditionalOptions(offerKeys: any) {
        try {
            const options = await Promise.all(
                offerKeys.map((offerKey: any) => appInstance.$api.additionalOptions.get({offerKey}))
            )
            SET_BOOKING_ADDITIONAL_OPTIONS(options)
        } catch {
            SET_BOOKING_ADDITIONAL_OPTIONS([])
        }
    }

    function serverInit() {
        SET_SEARCH_REQUEST(blankSearchRequest())
    }

    const extraMealTypes = computed(() => {
        return bookingAdditionalOptions.value.length
            ? bookingAdditionalOptions.value.map(options => options.alternativeMeals || [])
            : []
    })

    const searchRequestTourists = computed(() => {
        return hotelsRuntimeStore.searchRequestTouristsJSON(searchRequest.value)
    })

    const singleHotelSearchRequestTourists = computed(() => {
        return singleHotelSearchRequest.value
            ? hotelsRuntimeStore.searchRequestTouristsJSON(singleHotelSearchRequest.value)
            : []
    })

    const hasSingleHotelOffers = computed(() => {
        return !!singleHotelSearchResponse.value.offers.length
    })

    const basketItem = computed(() => {
        return (offerKeys: any) => basket.value.find(item => arrayEquals(item.offerKeys, offerKeys)) || {}
    })

    const hasOffers = computed(() => {
        return searchResponse.value.offersCount > 0
    })

    //TODO need refactoring with productStoreBase
    const orderTotalPrice = computed(() => {
        return (roomOffers: any, prebooked: any, selectedAddOns = [], offersData = [], prepareBookResponse: any) => {
            if (prebooked && prepareBookResponse) {
                const result = prepareBookResult(prepareBookResponse, offersData)
                const clientPrice = calcTotalPrice(result.map(({price}: any) => price)),
                    supplierPrice = calcTotalPrice(result.map(({supplierPrice}: any) => supplierPrice))

                const paidByLoyalty = result.reduce((sum, r) => {
                    sum += r.paymentByLoyaltyPoint?.paidByLoyalty || 0
                    return sum
                }, 0)
                return {
                    amount: clientPrice.amount,
                    commission: clientPrice.commission?.amount || 0,
                    currency: clientPrice.currency,
                    originalAmount: clientPrice.amount,
                    originalCurrency: clientPrice.currency,
                    ...(paidByLoyalty && {
                        paidByLoyalty,
                    }),
                    supplierPriceAmount: supplierPrice.amount,
                    supplierPriceOriginalCurrencyAmount: supplierPrice.amount,
                    supplierCommission: supplierPrice.commission?.amount || 0,
                    ...(paidByLoyalty && {
                        loyaltyAmount: paidByLoyalty,
                    }),
                }
            }
            //TODO Remove prebook check and simplify and refactoring
            const originalCurrency = roomOffers[0].price.originalCurrency || roomOffers[0].price.currency
            const totalRoomsPrice = roomOffers.reduce(
                (total: any, {price, currentPrice, supplierPrice}: any) => {
                    const offerPrice = prebooked && currentPrice ? currentPrice : price
                    total.amount += convertPrice(offerPrice).amount
                    if (offerPrice.commission) {
                        total.commission += convertPrice(offerPrice?.commission)?.amount
                    }
                    const roomOriginalCurrency = offerPrice.originalCurrency || offerPrice.currency,
                        roomOriginalAmount = offerPrice.originalAmount || offerPrice.amount
                    if (roomOriginalCurrency === originalCurrency) {
                        total.originalAmount += roomOriginalAmount
                    }

                    const supplierPriceObj = supplierPrice?.originalAmount
                        ? {amount: supplierPrice?.originalAmount, currency: supplierPrice?.originalCurrency}
                        : {amount: supplierPrice?.amount, currency: supplierPrice?.currency}
                    const convertedSupplierPrice = convertPrice(
                        supplierPriceObj,
                        persistentStore.getCurrency(originalCurrency)
                    )
                    const convertedSupplierPriceOriginal = convertPrice(supplierPriceObj, originalCurrency)

                    total.supplierPriceAmount += convertedSupplierPrice?.amount
                    total.supplierPriceOriginalCurrencyAmount += convertedSupplierPriceOriginal?.amount

                    if (supplierPrice?.commission?.originalAmount || supplierPrice?.commission?.amount) {
                        total.supplierCommission +=
                            supplierPrice?.commission.originalAmount || supplierPrice?.commission.amount
                    }
                    total.loyaltyAmount += offerPrice.loyaltyAmount
                    total.loyaltyPoints += offerPrice.loyaltyAmount
                    return total
                },
                {
                    amount: 0,
                    commission: 0,
                    currency: persistentStore.getCurrency(originalCurrency),
                    originalAmount: 0,
                    originalCurrency,
                    loyaltyPoints: 0,
                    supplierPriceAmount: 0,
                    supplierPriceOriginalCurrencyAmount: 0,
                    supplierCommission: 0,
                    loyaltyAmount: 0,
                }
            )
            for (const {
                product: [{offers}],
            } of <any>selectedAddOns.filter(({free}) => !free)) {
                for (const {price, loyaltyPoints, supplierPrice} of offers) {
                    totalRoomsPrice.amount += convertPrice(price).amount
                    if (price.commission) {
                        totalRoomsPrice.commission += convertPrice(price.commission).amount
                    }
                    if (originalCurrency === price.currency) {
                        totalRoomsPrice.originalAmount += price.amount
                    } else {
                        totalRoomsPrice.originalAmount += convertPrice(price, originalCurrency).amount
                    }
                    totalRoomsPrice.loyaltyPoints += loyaltyPoints || 0
                    totalRoomsPrice.loyaltyAmount += price.loyaltyAmount || 0
                    totalRoomsPrice.supplierPriceAmount += supplierPrice?.originalAmount || supplierPrice?.amount
                    if (supplierPrice?.commission?.originalAmount || supplierPrice?.commission?.amount) {
                        totalRoomsPrice.supplierCommission +=
                            supplierPrice?.commission.originalAmount || supplierPrice?.commission.amount
                    }
                }
            }
            if (prebooked) return totalRoomsPrice
            checkInOutPrice.value(totalRoomsPrice)
            return totalRoomsPrice
        }
    })

    const extraServices = computed(() => {
        return bookingAdditionalOptions.value.length
            ? bookingAdditionalOptions.value.map(options => options.extraServices || [])
            : []
    })

    const checkInOutPrice = computed(() => {
        return (total: any) => {
            return checkInOutExtraService.value.reduce((total, {earlyCheckIn, lateCheckOut}) => {
                const serviceOriginalCurrencyIn = earlyCheckIn?.price?.currency
                const serviceOriginalCurrencyOut = lateCheckOut?.price?.currency
                const serviceOriginalCurrency = serviceOriginalCurrencyIn || serviceOriginalCurrencyOut

                const amountIn = earlyCheckIn?.price ? convertPrice(earlyCheckIn.price).amount : 0
                const amountOut = lateCheckOut?.price ? convertPrice(lateCheckOut.price).amount : 0

                const originalCheckInAmount = earlyCheckIn?.price?.amount || 0
                const originalCheckOutAmount = lateCheckOut?.price?.amount || 0

                total.amount = total.amount + amountIn + amountOut
                total.supplierPriceAmount += originalCheckInAmount + originalCheckOutAmount

                if (total.originalCurrency) {
                    if (serviceOriginalCurrency === total.originalCurrency) {
                        total.originalAmount = total.originalAmount + originalCheckInAmount + originalCheckOutAmount
                    }
                }
                return total
            }, total)
        }
    })

    const category = computed(() => {
        return (stdCategory: any) => {
            switch (stdCategory) {
                case 'ONE':
                    return 1
                case 'ONE_AND_HALF':
                    return 1.5
                case 'TWO':
                    return 2
                case 'TWO_AND_HALF':
                    return 2.5
                case 'THREE':
                    return 3
                case 'THREE_AND_HALF':
                    return 3.5
                case 'FOUR':
                    return 4
                case 'FOUR_AND_HALF':
                    return 4.5
                case 'FIVE':
                    return 5
                default:
                    return null
            }
        }
    })

    return {
        ...useProductStore(searchResponse),
        searchRequest,
        searchResponse,
        searchExpirationTime,
        filters,
        sortFnName,
        groupOffersBy,
        singleHotelSearchRequest,
        singleHotelSearchExpirationTime,
        singleHotelSearchResponse,
        conditions,
        tariffDetails,
        tourists,
        prepareBookRequest,
        prepareBookResponse,
        offersToCompare,
        bookingAdditionalOptions,
        basket,
        checkInOutExtraService,
        mapPosition,
        recentHotels,
        SET_SEARCH_REQUEST,
        RESET_FILTERS,
        SET_SORT_FN_NAME,
        SET_GROUP_OFFERS_BY,
        SET_FILTER,
        SET_SINGLE_HOTEL_SEARCH_REQUEST,
        SET_SINGLE_HOTEL_SEARCH_RESPONSE,
        SET_CONDITIONS,
        SET_TARIFF_DETAILS,
        SET_TOURIST_PROP,
        SET_TOURIST_PASSPORT_PROP,
        SET_TOURIST,
        SET_TOURISTS,
        SET_PREPARE_BOOK_REQUEST,
        SET_PREPARE_BOOK_REQUEST_PROP,
        SET_PREPARE_BOOK_RESPONSE,
        SET_SEARCH_EXPIRATION_TIME,
        SET_SINGLE_HOTEL_SEARCH_EXPIRATION_TIME,
        SELECT_TO_COMPARE,
        ADD_ROOM_TO_COMPARE,
        CLEAR_OFFERS_TO_COMPARE,
        SET_BOOKING_ADDITIONAL_OPTIONS,
        SET_CHECK_IN_OUT_EXTRA_SERVICE,
        DELETE_CHECK_IN_OUT_EXTRA_SERVICE,
        ADD_TO_BASKET,
        REFRESH_BASKET_PRICE,
        REFRESH_BASKET_ROOM_OFFERS,
        NEW_SEARCH,
        RESET,
        RESET_SEARCH,
        SET_MAP_POSITION,
        NEW_SINGLE_HOTEL_SEARCH,
        SET_RECENT_HOTELS,
        clearBookingAdditionalOptions,
        getTariffDetails,
        prepareBook,
        loadBookingAdditionalOptions,
        serverInit,
        extraMealTypes,
        searchRequestTourists,
        singleHotelSearchRequestTourists,
        hasSingleHotelOffers,
        basketItem,
        hasOffers,
        orderTotalPrice,
        extraServices,
        checkInOutPrice,
        category,
    }
})

// TODO [P3] Need check this HRM improvement. Relates with https://pinia.vuejs.org/cookbook/hot-module-replacement.html#HMR-Hot-Module-Replacement-
if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(useHotelsStore, import.meta.hot))
